import React from "react";
import {
  Box,
  Button,
  Container,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import { Link as RouterLink } from "react-router-dom";
import colors from "../assets/theme/colors";
import { landingPageStyles } from "../assets/theme/landingTheme";
import Footer from "../components/Landing/Footer";
import BrandWordmark from "../components/UI/BrandWordmark";
import brandConfig from "../shared/brandConfig.json";

const UPDATED_DATE = "June 6, 2026";

const KEY_POINTS = [
  "We collect information you provide, including account details and care information you choose to save.",
  "Care notes, health details, medications, routines, and related records may be sensitive information.",
  "AI-assisted features help organize text and care observations, but Lifelog is still a caregiver-led service.",
  "We do not sell personal information, and we only share information as needed to provide, secure, or support the service.",
];

const POLICY_SECTIONS = [
  {
    title: "1. Services covered",
    body: [
      "This Privacy Policy applies when you visit lifelog.care, use Lifelog, contact us for support, or interact with us in other related ways.",
      "Lifelog is a caregiver logging and coordination platform that helps families capture day-to-day observations, behaviors, sleep, medications, routines, and other care details in a shared timeline.",
      "Caregivers can invite family members, therapists, teachers, and other care team members to view or contribute to a shared care log based on permissions.",
      "Information is entered voluntarily by users and is used to provide the logging, organization, and care coordination service.",
    ],
  },
  {
    title: "2. Information we collect from you",
    body: [
      "Account details, such as your name, email address, sign-in information, authentication data, and basic profile settings.",
      "Care profile information you choose to add, including names, ages, routines, goals, medications, support strategies, preferences, and care team details.",
      "Timeline and source information, such as notes, photos, documents, imports, extracted text, original words, and files you save in Lifelog.",
      "Support and communication information when you contact us, respond to feedback requests, or ask for help.",
    ],
  },
  {
    title: "3. Sensitive care information",
    body: [
      "Lifelog may process health and care-related information, including observations, behaviors, sleep, medication details, routines, support needs, school or therapy context, and other information you choose to record.",
      "We process sensitive information when you provide it, when it is needed to deliver the service, with your consent where required, or as otherwise allowed by applicable law.",
      "You should only enter information about another person when you have the right authority or permission to do so.",
    ],
  },
  {
    title: "4. Information collected automatically",
    body: [
      "We may collect technical information when you use the service, such as IP address, browser and device characteristics, operating system, language preferences, referring URLs, approximate location signals, and usage timestamps.",
      "We may collect log, diagnostic, performance, crash, and security information so we can operate, troubleshoot, protect, and improve Lifelog.",
      "This information is primarily used to maintain service security and reliability, understand usage trends, and support internal reporting.",
    ],
  },
  {
    title: "5. Cookies and similar technologies",
    body: [
      "We may use cookies or similar technologies to keep the service working, maintain account security, prevent crashes, fix bugs, save preferences, and support basic site functions.",
      "Most browsers let you remove or reject cookies, but some parts of Lifelog may not work properly without them.",
      "We do not sell personal information. If future optional analytics or marketing technologies require additional choices under applicable law, we will provide those choices as required.",
    ],
  },
  {
    title: "6. Google, sign-in, and connected account information",
    body: [
      "If you register or sign in using Google or another supported provider, we may receive profile information such as your name, email address, account identifier, and profile image, depending on that provider and your settings.",
      "We use sign-in information to create and secure your account, authenticate you, and provide the service.",
      "Our use of information received from Google APIs is intended to comply with the Google API Services User Data Policy, including its Limited Use requirements.",
    ],
  },
  {
    title: "7. How we use information",
    body: [
      "To create, authenticate, and manage user accounts.",
      "To provide Lifelog, save care records, organize source notes into timeline information, and keep care details connected to the right profile.",
      "To let invited care circle members view, contribute to, or coordinate around care information based on the access a Care Owner gives them.",
      "To respond to support requests, send administrative notices, request feedback, maintain service security, prevent fraud or misuse, identify usage trends, and improve the app.",
      "To comply with legal obligations, enforce terms, resolve disputes, and protect rights, safety, and vital interests where necessary.",
    ],
  },
  {
    title: "8. AI-assisted features",
    body: [
      "Lifelog may use artificial intelligence, machine learning, or similar technologies to help with text analysis, care-note organization, classification, and insights.",
      "When you upload, paste, or type information, Lifelog may process it to extract text, identify possible care details, and suggest structured timeline updates.",
      "AI-assisted features may use service providers such as Anthropic and Google Cloud AI, along with supporting cloud infrastructure, to process input, output, and related information for the service.",
      "AI-assisted organization is meant to reduce manual sorting. You should review sensitive information, especially medication, medical, legal, or safety details, before relying on it.",
      "You can contact us to ask about limiting AI-assisted processing, though some AI-assisted features may not be available if processing is limited.",
    ],
  },
  {
    title: "9. Legal bases for processing",
    body: [
      "Where privacy laws require a legal basis, we process personal information based on consent, performance of a contract, legitimate interests, legal obligations, and vital interests.",
      "Examples include providing the service you requested, securing the app, improving user experience, responding to support requests, complying with law, and protecting people from harm.",
      "If we rely on consent, you may withdraw consent where applicable, but withdrawal will not affect processing that already occurred or processing allowed under another legal basis.",
    ],
  },
  {
    title: "10. How information is shared",
    body: [
      "Care information is shared with people you invite or approve for a profile, according to their role and permissions.",
      "We may share information with trusted service providers that help us operate Lifelog, such as hosting, authentication, storage, databases, email, analytics, security, crash reporting, and AI processing providers.",
      "Current infrastructure and processing providers may include Google Cloud Platform, Cloud Firestore, Firebase Hosting, Firebase Realtime Database, Firebase crash reporting tools, Google account services, Anthropic, and Google Cloud AI.",
      "We may share or transfer information in connection with a business transaction, such as a merger, financing, acquisition, or sale of assets.",
      "We may disclose information if required by law, to protect rights and safety, to investigate misuse, or to comply with valid legal process.",
      "We do not sell personal information, and we have not sold or shared personal information for cross-context behavioral advertising in the preceding twelve months.",
    ],
  },
  {
    title: "11. How long we keep information",
    body: [
      "We keep personal information for as long as needed to provide the service, maintain accounts, comply with legal obligations, resolve disputes, protect safety, and maintain trustworthy records.",
      "When we no longer have an ongoing legitimate need to process information, we will delete or anonymize it where reasonably possible.",
      "Some information may remain temporarily in backups or security logs until it can be safely deleted according to normal retention practices.",
    ],
  },
  {
    title: "12. Security",
    body: [
      "We use reasonable technical and organizational safeguards designed to protect the personal information we process.",
      "No electronic transmission or storage system can be guaranteed to be completely secure, so you should use the service only within a secure environment and protect your account credentials.",
      "We may use diagnostic, crash, fraud-prevention, and security tools to protect accounts and maintain service reliability.",
    ],
  },
  {
    title: "13. Children, minors, and care recipients",
    body: [
      "Lifelog is designed for adults, caregivers, and authorized care teams. It is not intended for children to create accounts or use directly without appropriate consent and supervision.",
      "Adults may enter information about children, aging parents, patients, or other people they support when they have the right authority or permission to do so.",
      "If we learn that a child created an account or provided personal information without required consent, we will take reasonable steps to deactivate the account and delete the information.",
    ],
  },
  {
    title: "14. Your privacy rights and choices",
    body: [
      "Depending on where you live, you may have rights to access, correct, update, delete, export, restrict, or object to certain processing of your personal information.",
      "You may also have rights to withdraw consent, opt out of certain processing, limit use or disclosure of sensitive personal information, designate an authorized agent, appeal a denied request, or avoid discrimination for exercising privacy rights.",
      "You can update many account and care details in the app. Care Owners can manage who has access to a profile.",
      "To make a privacy request, contact us using the email below. We may need to verify your identity, account access, or authority before completing a request.",
    ],
  },
  {
    title: "15. Regional privacy rights",
    body: [
      "Residents of certain US states may have additional rights regarding access, correction, deletion, portability, opt-out choices, sensitive personal information, profiling, and authorized agents.",
      "People in the European Economic Area, United Kingdom, Switzerland, and Canada may have additional rights under local privacy laws, including access, correction, erasure, restriction, portability, objection, complaint, and consent-withdrawal rights.",
      "People in other regions, including Australia, New Zealand, and South Africa, may also have rights to access, correct, or complain about the handling of personal information under applicable local laws.",
      "We will consider and respond to privacy requests according to the laws that apply to the request and to Lifelog.",
    ],
  },
  {
    title: "16. Do Not Track",
    body: [
      "Some browsers and devices provide Do Not Track signals. Because there is not a consistent legal or technical standard for recognizing those signals, Lifelog does not currently respond to them.",
      "If a standard is adopted that applies to us, we will update this policy to explain how we respond.",
    ],
  },
  {
    title: "17. Policy updates",
    body: [
      "We may update this Privacy Policy from time to time. The updated version will show a new updated date at the top of the page.",
      "If we make material changes, we may notify users by posting a notice in the service or by sending a direct notification where appropriate.",
      "We encourage you to review this page periodically so you understand how Lifelog handles information.",
    ],
  },
];

const PrivacyPolicyPage = () => (
  <Box sx={landingPageStyles} data-cy="privacy-policy-page">
    <Box
      sx={{
        borderBottom: `1px solid ${colors.landing.borderSoft}`,
        bgcolor: alpha(colors.semantic.surface, 0.78),
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2.25, md: 4 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ minHeight: { xs: 72, md: 82 } }}
        >
          <Button
            component={RouterLink}
            to="/"
            aria-label="Lifelog home"
            sx={{
              p: 0,
              minWidth: "auto",
              textTransform: "none",
              "&:hover": { bgcolor: "transparent" },
            }}
          >
            <BrandWordmark variant="navbar" showDot={false} />
          </Button>
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ChevronLeftRoundedIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              color: colors.semantic.ink,
              borderRadius: "999px",
              px: 1.6,
            }}
          >
            Back to home
          </Button>
        </Stack>
      </Container>
    </Box>

    <Container maxWidth="lg" sx={{ px: { xs: 2.25, md: 4 }, py: { xs: 4, md: 6 } }}>
      <Box
        sx={{
          p: { xs: 2.4, md: 4 },
          borderRadius: "28px",
          bgcolor: colors.semantic.surface,
          border: `1px solid ${colors.landing.borderLight}`,
          boxShadow: `0 20px 54px ${alpha(colors.semantic.ink, 0.06)}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 850,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: colors.semantic.sageDeep,
          }}
        >
          Privacy Policy
        </Typography>
        <Typography
          component="h1"
          sx={{
            mt: 1,
            fontFamily: "'Outfit', sans-serif",
            fontSize: { xs: "2.35rem", md: "3.1rem" },
            lineHeight: 1.05,
            fontWeight: 850,
            color: colors.landing.heroText,
            letterSpacing: 0,
          }}
        >
          How Lifelog handles your care information.
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            maxWidth: 780,
            color: colors.landing.textMuted,
            fontSize: { xs: "1rem", md: "1.08rem" },
            lineHeight: 1.65,
          }}
        >
          Lifelog is built around sensitive daily care details. This policy explains what we collect, how we use it, and the choices you have.
        </Typography>
        <Typography sx={{ mt: 1.5, color: colors.landing.textSoft, fontWeight: 750 }}>
          Last updated: {UPDATED_DATE}
        </Typography>

        <Box
          sx={{
            mt: 3,
            p: { xs: 1.8, md: 2.2 },
            borderRadius: "22px",
            bgcolor: colors.brand.tint,
            border: `1px solid ${colors.landing.borderLight}`,
          }}
        >
          <Typography sx={{ fontWeight: 900, color: colors.semantic.ink, fontSize: "1.2rem" }}>
            At a glance
          </Typography>
          <Stack component="ul" spacing={0.8} sx={{ mt: 1.1, pl: 2.5, mb: 0 }}>
            {KEY_POINTS.map((item) => (
              <Typography
                component="li"
                key={item}
                sx={{
                  color: colors.landing.textMuted,
                  lineHeight: 1.62,
                  pl: 0.4,
                }}
              >
                {item}
              </Typography>
            ))}
          </Stack>
        </Box>

        <Stack spacing={2.2} sx={{ mt: { xs: 3, md: 4 } }}>
          {POLICY_SECTIONS.map((section) => (
            <Box
              key={section.title}
              sx={{
                p: { xs: 1.8, md: 2.2 },
                borderRadius: "20px",
                bgcolor: alpha(colors.semantic.sageMist, 0.5),
                border: `1px solid ${colors.landing.borderLight}`,
              }}
            >
              <Typography
                component="h2"
                sx={{
                  fontSize: { xs: "1.25rem", md: "1.42rem" },
                  fontWeight: 900,
                  color: colors.semantic.ink,
                }}
              >
                {section.title}
              </Typography>
              <Stack component="ul" spacing={0.8} sx={{ mt: 1.1, pl: 2.5, mb: 0 }}>
                {section.body.map((item) => (
                  <Typography
                    component="li"
                    key={item}
                    sx={{
                      color: colors.landing.textMuted,
                      lineHeight: 1.62,
                      pl: 0.4,
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>

        <Box
          sx={{
            mt: 3,
            p: { xs: 2, md: 2.4 },
            borderRadius: "22px",
            bgcolor: colors.brand.tint,
            border: `1px solid ${colors.landing.borderLight}`,
          }}
        >
          <Typography sx={{ fontWeight: 900, color: colors.semantic.ink, fontSize: "1.2rem" }}>
            Questions or requests
          </Typography>
          <Typography sx={{ mt: 0.7, color: colors.landing.textMuted, lineHeight: 1.62 }}>
            Contact us at{" "}
            <Link href={`mailto:${brandConfig.supportEmail}`} sx={{ color: colors.brand.deep, fontWeight: 850 }}>
              {brandConfig.supportEmail}
            </Link>
            . We may need to verify your identity or account access before helping with privacy requests.
          </Typography>
        </Box>
      </Box>
    </Container>

    <Footer variant="desktop" />
  </Box>
);

export default PrivacyPolicyPage;
